import { X } from 'lucide-react';
import { Dialog as Primitive } from 'radix-ui';
import { type ReactNode } from 'react';

import { cn } from './cn.ts';

export interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Always required: it names the dialog for screen readers. */
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  /** Buttons, right-aligned at the bottom. */
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** False for decisions that must be made explicitly (no Escape, no outside click). */
  dismissible?: boolean;
  className?: string;
}

const SIZES = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-3xl', xl: 'max-w-6xl' };

/**
 * A modal dialog. Radix traps focus inside it, returns focus to where it came from,
 * and hides the page behind from screen readers while it is open.
 */
export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  size = 'md',
  dismissible = true,
  className,
}: DialogProps) {
  return (
    <Primitive.Root open={open} onOpenChange={onOpenChange}>
      <Primitive.Portal>
        <Primitive.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-[1px]" />
        <Primitive.Content
          {...(dismissible
            ? {}
            : {
                onEscapeKeyDown: (event: KeyboardEvent) => {
                  event.preventDefault();
                },
                onPointerDownOutside: (event: Event) => {
                  event.preventDefault();
                },
              })}
          className={cn(
            'fixed top-1/2 left-1/2 z-50 flex max-h-[90dvh] w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 flex-col',
            'rounded-xl border border-border bg-surface-raised shadow-2xl focus:outline-none',
            SIZES[size],
            className,
          )}
          {...(description ? {} : { 'aria-describedby': undefined })}
        >
          <div className="flex items-start gap-4 border-b border-border px-5 py-4">
            <div className="min-w-0 flex-1">
              <Primitive.Title className="text-base font-semibold text-fg">{title}</Primitive.Title>
              {description ? (
                <Primitive.Description className="mt-1 text-sm text-fg-muted">
                  {description}
                </Primitive.Description>
              ) : null}
            </div>
            {dismissible ? (
              <Primitive.Close
                aria-label="Close"
                className="rounded-md p-1 text-fg-muted hover:bg-surface-hover hover:text-fg"
              >
                <X size={16} aria-hidden />
              </Primitive.Close>
            ) : null}
          </div>
          {children ? (
            <div className="min-h-0 flex-1 overflow-auto px-5 py-4">{children}</div>
          ) : null}
          {footer ? (
            <div className="flex flex-wrap justify-end gap-2 border-t border-border px-5 py-3">
              {footer}
            </div>
          ) : null}
        </Primitive.Content>
      </Primitive.Portal>
    </Primitive.Root>
  );
}
